'use client';
import { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CallToActionButton } from '@/components/shared/CallToActionButton';

function PromoBanner({ text, callToAction }) {
    const [isMounted, setIsMounted] = useState(false);
    const [portalElement, setPortalElement] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const bannerRef = useRef(null);
    const originalPaddingTopRef = useRef(null);

    const updatePadding = useCallback(() => {
        if (bannerRef.current) {
            const bannerHeight = bannerRef.current.offsetHeight;
            // Restore to original before recomputing so repeated calls don't stack.
            document.body.style.paddingTop = originalPaddingTopRef.current ?? '';
            const basePadding = parseFloat(window.getComputedStyle(document.body).paddingTop) || 0;
            document.body.style.paddingTop = `${basePadding + bannerHeight}px`;
        }
    }, []);

    useEffect(() => {
        originalPaddingTopRef.current = document.body.style.paddingTop;

        setPortalElement(document.body);
        setIsMounted(true);

        const timeoutId = setTimeout(updatePadding, 0);

        window.addEventListener('resize', updatePadding);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', updatePadding);
            document.body.style.paddingTop = originalPaddingTopRef.current ?? '';
        };
    }, [updatePadding]);

    const handleClose = () => {
        document.body.style.paddingTop = originalPaddingTopRef.current ?? '';
        setIsVisible(false);
    };

    if (!isMounted || !portalElement || !isVisible) return null;

    const [button] = callToAction ?? [];

    const bannerContent = (
        <>
            <div className="fixed inset-x-0 top-0 z-40 w-full">
                <div className="relative text-xs md:text-sm">
                    <div
                        ref={bannerRef}
                        data-component="PromoBanner"
                        className="flex items-center justify-center bg-indigo-700 pb-6 pt-4 text-center text-white">
                        <div className="mr-1 max-w-[40%] md:max-w-full">{text}</div>
                        {button && button.urlName && button.url && (
                            <CallToActionButton
                                className={
                                    '!mx-0 !min-h-[44px] border-none !bg-indigo-700 px-3 py-2 underline hover:text-fuchsia-700'
                                }
                                {...button}>
                                {button.urlName}
                            </CallToActionButton>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="absolute right-2 top-1/2 flex min-h-[44px] min-w-[44px] -translate-y-1/2 items-center justify-center rounded-full bg-indigo-700 sm:right-2 md:right-3"
                        aria-label="Close promo banner">
                        <div className="relative size-6 hover:cursor-pointer">
                            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 rotate-45 bg-white"></div>
                            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 -rotate-45 bg-white"></div>
                        </div>
                    </button>
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(bannerContent, portalElement);
}

export default PromoBanner;
